import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Search, 
  Eye, 
  RefreshCw,
  Building2,
  Calendar,
  IndianRupee,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const BusinessSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [columnFilters, setColumnFilters] = useState({
    business: '',
    plan: '',
    status: []
  });

  const searchTimerRef = useRef(null);

  const fetchSubscriptions = useCallback(async (search = '') => {
    try {
      setLoading(true);

      const params = { limit: 100 };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (search) {
        params.search = search;
      }

      const { data } = await api.get('/admin/businesses/with-entitlements', { params });
      // Transform to subscription-like format
      const subs = (data.businesses || []).map(b => ({
        id: b.id,
        business_id: b.id,
        business_name: b.name,
        plan_code: b.plan_code || 'BASIC',
        plan_name: b.plan_name || b.plan_code || 'Unknown Plan',
        subscription_status: b.subscription_status || 'active',
        max_staff: b.max_staff || 3,
        staff_used: b.staff_used || 0,
        max_tracking_staff: b.max_tracking_staff || 0,
        tracking_staff_used: b.tracking_staff_used || 0,
        field_tracking_enabled: b.field_tracking_enabled || false,
        created_at: b.created_at
      }));
      setSubscriptions(subs);
      setTotalCount(data.total || subs.length);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // Re-fetch when status filter changes (search uses its own debounce handler)
  useEffect(() => {
    fetchSubscriptions(searchTerm);
  }, [filterStatus]);

  // Debounced re-fetch when search text changes (400ms)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchSubscriptions(value);
    }, 400);
  };

  // Apply column-level filters client-side (text search is server-side)
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesBusiness = !columnFilters.business ||
      sub.business_name?.toLowerCase().includes(columnFilters.business.toLowerCase());

    const matchesPlan = !columnFilters.plan ||
      sub.plan_name?.toLowerCase().includes(columnFilters.plan.toLowerCase());

    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(sub.subscription_status);

    return matchesBusiness && matchesPlan && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: totalCount,
    active: subscriptions.filter(s => s.subscription_status === 'active').length,
    fieldTracking: subscriptions.filter(s => s.field_tracking_enabled).length
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
      trialing: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      suspended: { bg: 'bg-rose-100', text: 'text-rose-700', icon: AlertCircle },
      cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', icon: AlertCircle }
    };
    return badges[status] || badges.active;
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col" data-testid="subscriptions-page">
        <ListPageHeader title="Business Subscriptions" stats={[]} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-[3px] border-slate-200 border-t-rose-500 rounded-full animate-spin"></div>
            <p className="text-[13px] text-slate-500 font-normal">Loading subscriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="subscriptions-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Business Subscriptions"
        stats={[
          { icon: CreditCard, label: 'Total', value: stats.total, color: 'slate' },
          { icon: CheckCircle, label: 'Active', value: stats.active, color: 'emerald' },
          { icon: Building2, label: 'Field Tracking', value: stats.fieldTracking, color: 'teal' },
        ]}
        ctaLabel="Refresh"
        ctaIcon={RefreshCw}
        onCtaClick={() => fetchSubscriptions(searchTerm)}
        extraControls={
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search subscriptions..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        }
      />

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {filteredSubscriptions.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No subscriptions found</p>
              <p className="text-[13px] text-slate-400 mt-1">Subscriptions will appear here when businesses are created</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Single table -- colgroup enforces widths so header + body columns align */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{width: '22%'}} />
                    <col style={{width: '15%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '14%'}} />
                    <col style={{width: '14%'}} />
                    <col style={{width: '12%'}} />
                    <col style={{width: '10%'}} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          Business
                          <ColumnFilter
                            type="text"
                            value={columnFilters.business}
                            onChange={(val) => setColumnFilters({...columnFilters, business: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        <div className="flex items-center gap-1">
                          <CreditCard className="w-3.5 h-3.5" />
                          Plan
                          <ColumnFilter
                            type="text"
                            value={columnFilters.plan}
                            onChange={(val) => setColumnFilters({...columnFilters, plan: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Status
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Staff Usage
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Tracking Usage
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Created
                      </th>
                      <th className="tl-th" style={{background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'}}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredSubscriptions.map((sub, index) => {
                      const statusBadge = getStatusBadge(sub.subscription_status);
                      const StatusIcon = statusBadge.icon;
                      return (
                        <tr
                          key={sub.id}
                          className={`tl-tr hover:bg-rose-50/30 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-4 h-4 text-slate-500" />
                              </div>
                              <span className="text-[13px] font-medium text-slate-800 truncate">{sub.business_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${
                              sub.field_tracking_enabled
                                ? 'bg-teal-100 text-teal-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {sub.plan_name}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text} flex items-center gap-1 w-fit`}>
                              <StatusIcon className="w-3 h-3" />
                              {sub.subscription_status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-slate-100 rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full bg-blue-500 transition-all"
                                  style={{ width: `${Math.min((sub.staff_used / (sub.max_staff || 1)) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-[11px] text-slate-600">{sub.staff_used}/{sub.max_staff}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {sub.field_tracking_enabled ? (
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-slate-100 rounded-full h-1.5">
                                  <div
                                    className="h-1.5 rounded-full bg-teal-500 transition-all"
                                    style={{ width: `${Math.min((sub.tracking_staff_used / (sub.max_tracking_staff || 1)) * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-[11px] text-slate-600">{sub.tracking_staff_used}/{sub.max_tracking_staff}</span>
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-slate-500">
                            {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/admin/businesses/${sub.business_id}`}>
                              <button
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Manage Subscription"
                              >
                                <Eye className="w-4 h-4 text-slate-600" />
                              </button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="tl-table-footer">
                Showing {filteredSubscriptions.length} of {totalCount} subscriptions
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessSubscriptions;
